import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import {
  CONTRACTS,
  MAX_TOTAL_SPINS,
  SYMBOLS,
  TARGET_SCORE,
  STARTING_SPINS,
  getSpinPreview,
} from './game';

const EXAMPLES = [
  ['7', '7', '7'],
  ['0', '7', '7'],
  ['crown', 'wild', '2'],
  ['skull', 'skull', 'skull'],
];

const SlotGuide = ({ contracts, onClose }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const unlock = lockBodyScroll();
    if (dialogRef.current.showModal) dialogRef.current.showModal();
    else dialogRef.current.setAttribute('open', '');
    closeRef.current?.focus();
    return () => {
      unlock();
      previousFocus?.focus?.();
    };
  }, []);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
    if (event.key !== 'Tab') return;
    const items = dialogRef.current.querySelectorAll('button, [tabindex="0"]');
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <dialog
      className="slotGuideDialog"
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby="slot-guide-title"
      onKeyDown={onKeyDown}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          onClose();
      }}
    >
      <header className="slotGuideHeader">
        <h2 id="slot-guide-title">Symbols & scoring</h2>
        <button
          ref={closeRef}
          onClick={onClose}
          aria-label="Close symbol and scoring guide"
        >
          ×
        </button>
      </header>
      <div
        className="slotGuideBody"
        tabIndex={0}
        role="region"
        aria-label="Game rules"
      >
        <p>
          Reach <strong>{TARGET_SCORE} points</strong> while keeping at least 1
          HP. Start with {STARTING_SPINS} spins; bonus spins can extend the run
          to {MAX_TOTAL_SPINS}.
        </p>
        <h3>What each symbol means</h3>
        <dl className="slotGuideSymbols">
          <div>
            <dt>0–9 · Numbers</dt>
            <dd>
              Worth their face value. Seven also counts as a lucky symbol.
            </dd>
          </div>
          {['crown', 'eye', 'skull', 'wild'].map((id) => (
            <div key={id}>
              <dt>
                <span aria-hidden="true">{SYMBOLS[id].glyph}</span>{' '}
                {SYMBOLS[id].name} · {SYMBOLS[id].value} points
              </dt>
              <dd>
                {
                  {
                    crown: 'Counts toward the Crown bonus round.',
                    eye: 'Gets an extra payout with Eye of the House.',
                    skull: 'Two or more cause damage and reduce your payout.',
                    wild: 'Counts as lucky and toward the Crown bonus. Does not substitute for other symbols.',
                  }[id]
                }
              </dd>
            </div>
          ))}
        </dl>
        <h3>How points add up</h3>
        <p className="slotGuideFormula">
          Symbol values + combination bonuses + contract effects = payout
          (minimum 0).
        </p>
        <ul>
          <li>
            <strong>Exactly two identical symbols:</strong> +12 points. Matching
            values alone don’t count.
          </li>
          <li>
            <strong>Three identical symbols:</strong> +40 points and +1 spin,
            replacing the pair bonus. Triple Seven gets +80 instead. Triple
            Skull gets neither jackpot points nor a bonus spin.
          </li>
          <li>
            <strong>At least two Sevens/Wilds, in any mix:</strong> +15 points
            unless all three symbols are identical.
          </li>
          <li>
            <strong>At least two Crowns/Wilds, in any mix:</strong> +10 points
            and +1 spin. Two Wilds qualify without a Crown.
          </li>
          <li>
            <strong>Two or three Skulls:</strong> −5 points.
          </li>
        </ul>
        <p>
          Eligible bonuses stack. Each spin awards at most one bonus spin, even
          when multiple combinations qualify.
        </p>
        <h3>HP and nudges</h3>
        <p>
          A final payout below 10 costs 1 HP. Two Skulls cost another 1 HP;
          three cost another 2 HP. Contract damage adds on top. Reaching 0 HP
          ends the run, even if that spin reaches the score target.
        </p>
        <p>
          Spend a nudge to randomly reroll one reel before locking the payout.
          You start with two nudges. Signing a contract after spins 2, 4, and 6
          restores one, up to three.
        </p>
        <h3>Example payouts · without contracts</h3>
        <div className="slotGuideExamples">
          {EXAMPLES.map((reels) => {
            const preview = getSpinPreview({ reels, contracts: [] });
            return (
              <div key={reels.join('-')}>
                <strong
                  aria-label={reels.map((id) => SYMBOLS[id].name).join(', ')}
                >
                  {reels.map((id) => SYMBOLS[id].glyph).join(' ')}
                </strong>
                <span>
                  {preview.base} base{' '}
                  {preview.effects
                    .map(
                      (effect) =>
                        `${effect.amount < 0 ? '−' : '+'} ${Math.abs(
                          effect.amount
                        )}`
                    )
                    .join(' ')}{' '}
                  = <b>{preview.payout} points</b>
                  {preview.hpLoss > 0 ? ` · −${preview.hpLoss} HP` : ''}
                  {preview.bonusSpins ? ' · +1 spin' : ''}
                </span>
              </div>
            );
          })}
        </div>
        <h3>Contracts</h3>
        <p>
          Each signed contract lasts for the run. Its effects stack with every
          other signed contract.
        </p>
        <dl className="slotGuideContracts">
          {Object.entries(CONTRACTS).map(([id, contract]) => (
            <div key={id}>
              <dt>
                {contract.name}
                {contracts.includes(id) ? ' · SIGNED' : ''}
              </dt>
              <dd>
                {id === 'twinEngine'
                  ? 'A pair or triple pays +18.'
                  : contract.boon}{' '}
                {contract.curse}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </dialog>,
    document.body
  );
};

export default SlotGuide;
